import { computed, onUnmounted, reactive, ref, unref, watch } from 'vue'
import { CancelledError, useQuery, useQueryClient } from '@tanstack/vue-query'
import { useDebounceFn } from '@vueuse/core'
import { fetchCarById, fetchCarsList } from '@/backend/carsApi'
import {
  CARS_DETAIL_QUERY_KEY,
  CARS_LIST_QUERY_KEY,
  invalidateCarDetail,
  invalidateCarsQueries,
} from '@/queryClient'
import {
  buildCarsListParams,
  carsListItems,
  carsListParamsWithoutPage,
  defaultCarsFilters,
  loadCarsTableState,
  normalizeCarsFilters,
  normalizeCarsTableState,
  saveCarsFilters,
  saveCarsTableState,
} from './carsState.mjs'

function sameParams(a, b) {
  return JSON.stringify(a) === JSON.stringify(b)
}

/** Text search fields — only these are debounced; select/page commit immediately. */
const TEXT_PARAM_KEYS = ['search', 'number', 'vin', 'owner_phone']

function textParamsOnly(params = {}) {
  const out = {}
  TEXT_PARAM_KEYS.forEach((key) => {
    if (params[key] != null && params[key] !== '') out[key] = params[key]
  })
  return out
}

function isRequestAborted(error, signal) {
  return Boolean(
    signal?.aborted
    || error?.code === 'ERR_CANCELED'
    || error?.name === 'CanceledError'
    || error?.name === 'AbortError',
  )
}

function rethrowUnlessAborted(error, signal) {
  if (isRequestAborted(error, signal)) {
    throw new CancelledError({ silent: true })
  }
  throw error
}

/**
 * Список машин: TanStack Query + фильтры + table state.
 *
 * presetParams — фиксированные параметры запроса, которые нельзя сбросить из UI.
 * Например `{ is_trailer: true }` для страницы «Прицепы»: параметр вшивается в
 * каждый запрос и в queryKey, поэтому кэш не пересекается со списком машин.
 */
export function useCarsPage(initialFilters = {}, presetParams = {}) {
  const queryClient = useQueryClient()
  const filters = reactive(normalizeCarsFilters(initialFilters))
  const tableState = reactive(loadCarsTableState(window.localStorage))
  const lastTotalPages = ref(1)

  const buildParams = (f) => ({ ...presetParams, ...buildCarsListParams(f) })

  const requestParams = ref(buildParams(filters))

  const commitParams = (params) => {
    if (sameParams(requestParams.value, params)) return
    requestParams.value = params
  }

  const commitParamsDebounced = useDebounceFn(commitParams, 300)

  onUnmounted(() => {
    commitParamsDebounced.cancel?.()
  })

  watch(
    () => buildCarsListParams(filters),
    (next) => {
      const prev = requestParams.value
      // preset params не должны попадать в текстовую дебаунс-логику —
      // они не меняются, поэтому сравниваем только «пользовательскую» часть.
      const nextWithPreset = { ...presetParams, ...next }
      const onlyPageChanged =
        sameParams(
          carsListParamsWithoutPage(prev),
          carsListParamsWithoutPage(nextWithPreset),
        ) && prev.page !== next.page

      // Select filters (avtocod etc.) must apply immediately — debounce only typing.
      const onlyNonTextChanged = sameParams(textParamsOnly(prev), textParamsOnly(nextWithPreset))

      if (onlyPageChanged || onlyNonTextChanged || sameParams(prev, nextWithPreset)) {
        commitParamsDebounced.cancel?.()
        commitParams(nextWithPreset)
        return
      }
      commitParamsDebounced(nextWithPreset)
    },
  )

  watch(
    filters,
    () => {
      try {
        saveCarsFilters(window.localStorage, filters)
      } catch {
        // ignore
      }
    },
    { deep: true },
  )

  watch(
    tableState,
    () => {
      try {
        saveCarsTableState(window.localStorage, tableState)
      } catch {
        // ignore
      }
    },
    { deep: true },
  )

  const queryKey = computed(() => [CARS_LIST_QUERY_KEY, requestParams.value])

  const carsQuery = useQuery({
    queryKey,
    queryFn: async ({ signal, queryKey: key }) => {
      // Always use params from queryKey — never a mutable ref — to avoid
      // caching "не проверено" results under the "все записи" key.
      const params = key[1]
      try {
        return await fetchCarsList(params, { signal })
      } catch (error) {
        rethrowUnlessAborted(error, signal)
      }
    },
    staleTime: 15000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    placeholderData: (previousData, previousQuery) => {
      const previousParams = previousQuery?.queryKey?.[1]
      const currentParams = requestParams.value
      if (!previousParams || !previousData) return undefined
      return sameParams(
        carsListParamsWithoutPage(previousParams),
        carsListParamsWithoutPage(currentParams),
      )
        ? previousData
        : undefined
    },
  })

  watch(
    () => ({
      payload: carsQuery.data.value,
      isPlaceholder: carsQuery.isPlaceholderData.value,
    }),
    ({ payload, isPlaceholder }) => {
      // Placeholder keeps previous page payload — do not clamp against it.
      if (isPlaceholder || !payload) return
      const total = Number(payload?.total_pages)
      if (Number.isFinite(total) && total > 0) {
        lastTotalPages.value = total
        if (filters.page > total) {
          filters.page = total
        }
      }
    },
  )

  const setPage = (page) => {
    const max = Math.max(1, lastTotalPages.value || 1)
    const next = Math.max(1, Number(page) || 1)
    if (next === filters.page) return
    filters.page = Math.min(next, max)
  }

  const updateFilters = (patch) => {
    Object.assign(filters, patch, { page: 1 })
  }

  /** Мгновенная подмена фильтров (URL back/forward, сброс) без debounce. */
  const replaceFilters = (next) => {
    commitParamsDebounced.cancel?.()
    Object.assign(filters, normalizeCarsFilters(next))
    commitParams(buildParams(filters))
  }

  const resetFilters = () => {
    replaceFilters(defaultCarsFilters())
  }

  const resetTableState = () => {
    const next = normalizeCarsTableState({})
    tableState.columnOrder = next.columnOrder
    tableState.columnVisibility = { ...next.columnVisibility }
    tableState.columnSizing = { ...next.columnSizing }
  }

  return {
    filters,
    tableState,
    carsQuery,
    cars: computed(() => carsListItems(carsQuery.data.value)),
    totalPages: computed(() => {
      const pages = Number(carsQuery.data.value?.total_pages)
      if (Number.isFinite(pages) && pages > 0) return pages
      return lastTotalPages.value || 1
    }),
    currentPage: computed(() => filters.page || 1),
    isLoading: computed(
      () => carsQuery.isPending.value && !carsQuery.data.value,
    ),
    isFetching: computed(() => carsQuery.isFetching.value),
    isPlaceholderData: computed(() => Boolean(carsQuery.isPlaceholderData.value)),
    hasError: computed(() => carsQuery.isError.value),
    setPage,
    updateFilters,
    replaceFilters,
    resetFilters,
    resetTableState,
    invalidateCars: () => invalidateCarsQueries(queryClient),
  }
}

/**
 * Деталь машины по id (ref | number | string).
 */
export function useCarPage(carIdSource) {
  const queryClient = useQueryClient()
  const id = computed(() => {
    const raw = unref(carIdSource)
    const value = Number(raw)
    return Number.isFinite(value) ? value : NaN
  })

  const carQuery = useQuery({
    queryKey: computed(() => [CARS_DETAIL_QUERY_KEY, id.value]),
    queryFn: async ({ signal, queryKey: key }) => {
      const carId = key[1]
      try {
        return await fetchCarById(carId, { signal })
      } catch (error) {
        rethrowUnlessAborted(error, signal)
      }
    },
    enabled: computed(() => Number.isFinite(id.value) && id.value > 0),
    staleTime: 15000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  })

  return {
    carQuery,
    car: computed(() => carQuery.data.value || null),
    isLoading: computed(
      () => carQuery.isPending.value && !carQuery.data.value,
    ),
    isFetching: computed(() => carQuery.isFetching.value),
    hasError: computed(() => carQuery.isError.value),
    refetch: () => carQuery.refetch(),
    invalidateCar: () => invalidateCarDetail(id.value, queryClient),
  }
}
